<template>
  <v-menu
    content-class="rounded-lg mt-2"
    offset-y
    close-on-click
    :disabled="disabled || loading"
  >
    <template #activator="{ on, attrs }">
      <v-btn
        class="action-text text-none"
        :class="classes"
        :color="color"
        rounded
        small
        elevation="0"
        v-bind="attrs"
        v-on="on"
      >
        <v-icon class="material-icons-outlined" small>
          {{ icon }}
        </v-icon>
        <span v-if="showLabel" class="ml-2">{{ text }}</span>
        <v-icon v-if="!disabled && !loading">arrow_drop_down</v-icon>
        <v-progress-circular
          v-else-if="loading"
          indeterminate
          size="16"
          width="2"
          class="ml-2"
        />
      </v-btn>
    </template>
    <v-list rounded>
      <v-list-item
        v-for="option in options"
        :key="option"
        dense
        @click="onOptionSelected(option)"
      >
        <v-list-item-title>
          <v-icon
            left
            class="material-icons-outlined"
            small
            :color="getIconColorForClassification(option)"
          >
            {{ getIconForClassification(option) }}
          </v-icon>
          <span>
            {{ getTextForClassification(option) }}
          </span>
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
import { RelevanceAssessment, InclusionAssessment } from '@/enums';

const REVOKE = 'REVOKE';

export default {
  name: 'AssessmentDropdown',
  props: {
    value: {
      type: String,
      required: true,
      validator: (value) =>
        Object.values(RelevanceAssessment).includes(value) ||
        Object.values(InclusionAssessment).includes(value),
    },
    hideOptions: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showOutcomeIcons: {
      type: Boolean,
      default: false,
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
    inclusionAssessment: {
      type: Boolean,
      default: false,
    },
    showRevokeOption: {
      type: Boolean,
      default: false,
    },
    revokeOptionLabel: {
      type: String,
      default: 'Revoke',
    },
  },
  emits: ['input', 'revoke'],
  computed: {
    classes() {
      return this.disabled
        ? ['grey--text', 'text--darken-2']
        : this.styleProps.classes ?? [];
    },
    text() {
      return this.styleProps.text;
    },
    color() {
      return this.disabled ? 'grey lighten-2' : this.styleProps.color;
    },
    icon() {
      return this.styleProps.icon;
    },
    options() {
      const optionsEnum = this.inclusionAssessment
        ? InclusionAssessment
        : RelevanceAssessment;
      const options = Object.values(optionsEnum).filter(
        (option) =>
          option !== this.value && !this.hideOptions.includes(option)
      );
      if (this.showRevokeOption) {
        options.push(REVOKE);
      }
      return options;
    },
    styleProps() {
      return this.valueToStyleProps[this.value];
    },
    valueToStyleProps() {
      return {
        [RelevanceAssessment.RELEVANT]: {
          text: 'Relevant',
          color: 'success',
          textColor: 'white',
          icon: this.showOutcomeIcons ? 'check' : 'thumb_up_alt',
          iconColor: 'success',
        },
        [RelevanceAssessment.NOT_RELEVANT]: {
          text: 'Not Relevant',
          color: 'error',
          textColor: 'white',
          icon: this.showOutcomeIcons ? 'close' : 'thumb_down_alt',
          iconColor: 'error',
        },
        [RelevanceAssessment.NOT_SURE]: {
          classes: 'white--text',
          text: 'Not Sure',
          color: 'warning',
          icon: this.showOutcomeIcons ? 'find_in_page' : 'help',
          iconColor: 'warning',
        },
        [RelevanceAssessment.UNSCREENED]: {
          classes: 'grey--text text--darken-1',
          text: 'Unscreened',
          color: 'grey lighten-2',
          icon: 'inbox',
          iconColor: 'grey darken-1',
        },
        [InclusionAssessment.INCLUDE]: {
          text: 'Include',
          color: 'success',
          textColor: 'white',
          icon: this.showOutcomeIcons ? 'check' : 'thumb_up_alt',
          iconColor: 'success',
        },
        [InclusionAssessment.EXCLUDE]: {
          text: 'Exclude',
          color: 'error',
          textColor: 'white',
          icon: this.showOutcomeIcons
            ? 'remove_circle'
            : 'thumb_down_alt',
          iconColor: 'error',
        },
        [REVOKE]: {
          text: this.revokeOptionLabel,
          color: 'error',
          textColor: 'white',
          icon: 'undo',
          iconColor: 'error',
        },
      };
    },
  },
  methods: {
    onOptionSelected(option) {
      if (option === REVOKE) {
        this.$emit('revoke');
      } else {
        this.$emit('input', option);
      }
    },
    getTextForClassification(classification) {
      return this.valueToStyleProps[classification].text;
    },
    getColorForClassification(classification) {
      return this.valueToStyleProps[classification].color;
    },
    getIconForClassification(classification) {
      return this.valueToStyleProps[classification].icon;
    },
    getIconColorForClassification(classification) {
      return this.valueToStyleProps[classification].iconColor;
    },
  },
};
</script>
